import { BulkManagementTablePrimitives } from './components/BulkManagementTablePrimitives';
import React, { useMemo } from 'react';
import { type AnalysisSubject, useHasFF, withSuspense } from 'venn-utils';
import { useBulkManagementTable } from './hooks/useBulkManagementTable';
import { useAllocationColumns } from './hooks/useAllocationColumns';
import { BulkManagementLoadingState } from './BulkManagementLoadingState';
import { useInvestmentFilter } from './hooks/useInvestmentFilter';
import { getBulkManagementData } from './data';
import { isBulkManageLotRow } from './utils';

type Props = {
  subject: AnalysisSubject;
};
export const BulkAllocationManagementTable = withSuspense(
  BulkManagementLoadingState,
  function BulkAllocationManagementTable({ subject }: Props) {
    const {
      bulkManagementTableModals,
      openedSecondaryId,
      uniqFunds,
      selectedFunds,
      onProxyChange,
      rangeAnalysis,
      proxyDataByFund,
      holdingsProxyDataByFund,
      rangeDataByFund,
      benchmarkRange,
      onClearSelected,
      gridRef,
      onSelectionChanged,
      groupHeaderHeight,
      rowClassRules,
      selectedAllocationDate,
      allocationDates,
      onSetSetSelectedDate,
      showLiquidatedInvestments,
      benchmarkInfo,
      rangeData,
      primaryFactorLens,
      cashRateRange,
      referenceRateRange,
    } = useBulkManagementTable({
      subject,
      shouldHighlightRowsWithHistoricalErrors: false,
    });

    const hasTaxImpact = useHasFF('tax_impact_ff');

    const { rowData, pinnedRowData } = useMemo(
      () =>
        getBulkManagementData(
          subject,
          uniqFunds,
          proxyDataByFund,
          holdingsProxyDataByFund,
          rangeDataByFund,
          benchmarkRange,
          benchmarkInfo.benchmark,
          rangeData,
          primaryFactorLens,
          cashRateRange,
          referenceRateRange,
          allocationDates,
          true,
          hasTaxImpact,
        ),
      [
        subject,
        uniqFunds,
        proxyDataByFund,
        holdingsProxyDataByFund,
        rangeDataByFund,
        benchmarkRange,
        benchmarkInfo.benchmark,
        rangeData,
        primaryFactorLens,
        cashRateRange,
        referenceRateRange,
        allocationDates,
        hasTaxImpact,
      ],
    );

    const hasTaxInformation = useMemo(() => rowData.some((row) => isBulkManageLotRow(row)), [rowData]);

    const { columnDefs, autoGroupColumnDef } = useAllocationColumns({
      subject,
      portfolio: subject?.portfolio,
      allocationDates,
      selectedAllocationDate,
      onSetSetSelectedDate,
      hasTaxInformation,
    });

    const { isExternalFilterPresent, doesExternalFilterPass } = useInvestmentFilter({
      portfolio: subject?.portfolio,
      selectedAllocationDate,
      allocationDates,
      gridApi: gridRef.current?.api,
      showAllocations: true,
      showLiquidatedInvestments,
      showLiquidatedInvestmentsToggle: false,
    });

    return (
      <BulkManagementTablePrimitives.Root
        openedSecondaryId={openedSecondaryId}
        uniqFunds={uniqFunds}
        selectedFunds={selectedFunds}
        onProxyChange={onProxyChange}
        disableSelection
        rangeAnalysis={rangeAnalysis}
        proxyDataByFund={proxyDataByFund}
        holdingsProxyDataByFund={holdingsProxyDataByFund}
        rangeDataByFund={rangeDataByFund}
        onClearSelected={onClearSelected}
        modals={bulkManagementTableModals}
      >
        <BulkManagementTablePrimitives.Grid
          gridRef={gridRef}
          rowData={rowData}
          pinnedRowData={pinnedRowData}
          columnDefs={columnDefs}
          onSelectionChanged={onSelectionChanged}
          groupHeaderHeight={groupHeaderHeight}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          disableSelection
          autoGroupColumnDef={autoGroupColumnDef}
          rowClassRules={rowClassRules}
          hasSelections={false}
          isAllocationsTable
        />
      </BulkManagementTablePrimitives.Root>
    );
  },
);
